import { requirePermission } from "@/lib/casl/route-guards";
import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { useRecordWebcam } from "react-record-webcam";
import { useEffect, useState } from "react";
import { MentorUpdatesRightPanel } from "@/components/mentor-monthly-updates/right-panel";
import { useNavigate } from "@tanstack/react-router";
import { SendConfirmationModal } from "@/components/mentor-monthly-updates/send-confirmation-modal";
import { Loader2 } from "lucide-react";
import { useGetMentorship } from "@/lib/api/mentorships";
import { fetchApi } from "@/lib/api";
import { useGetAccessRecords } from "@/lib/hooks/user/use-get-access-records";
import { RecordingPanel } from "@/components/mentor-monthly-updates/recording-panel";
import { useGetMentorMonthlyUpdate } from "@/lib/hooks/mentors/use-get-mentor-monthly-update";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { useGetMentorMonthlySummary } from "@/lib/hooks/mentors/use-get-monthly-summary";
import { useMutation } from "@tanstack/react-query";
import { useAbility } from "@/lib/casl/ability-context";
import { getMenteeDisplayName } from "@/lib/redacted-display";
import { useNotificationMuteActivity } from "@/lib/hooks/conversations/use-notification-mute-activity";

export const Route = createFileRoute(
  "/_authenticated/mentor-monthly-updates/$mentorshipId",
)({
  component: MentorMonthlyUpdates,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "mentorships");
  },
  validateSearch: (search: Record<string, unknown>) => {
    return {
      videoId: search.videoId as string | undefined,
    };
  },
});

function MentorMonthlyUpdates() {
  const ability = useAbility();
  const canUpdateMentorships = ability.can("update", "mentorships");
  const { mentorshipId } = useParams({
    strict: false,
  }) as { mentorshipId: string };

  const [showDeviceModal, setShowDeviceModal] = useState(true);
  const [recording, setRecording] = useState(false);
  const [isReplayMode, setIsReplayMode] = useState(false);
  const [duration, setDuration] = useState(0);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [videoDeviceId, setVideoDeviceId] = useState<string>("");
  const [audioDeviceId, setAudioDeviceId] = useState<string>("");
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [showRightPanel, setShowRightPanel] = useState(true);
  const [showSendConfirmation, setShowSendConfirmation] = useState(false);
  const navigate = useNavigate();

  useNotificationMuteActivity(recording, "mentor-monthly-update");

  const { videoId } = useSearch({ strict: false }) as { videoId?: string };

  const {
    data: mentorship,
    isPending,
    isError,
  } = useGetMentorship(mentorshipId);

  const {
    activeRecordings,
    createRecording,
    openCamera,
    closeCamera,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    download,
    clearAllRecordings,
    devicesByType,
  } = useRecordWebcam({
    options: { fileType: "mp4" },
    mediaRecorderOptions: { mimeType: "video/mp4;codecs=avc1" },
  });

  const [currentRecordingId, setCurrentRecordingId] = useState<string | null>(
    null,
  );
  const [isUploading, setIsUploading] = useState(false);

  const { data: accessRecords } = useGetAccessRecords(mentorship?.mentee?.id);

  const { data: mentorUpdate } = useGetMentorMonthlyUpdate(
    videoId,
    mentorshipId,
  );
  const { url: signedVideoUrl } = useSignedMedia(
    mentorUpdate?.data?.update?.location,
  );

  const { data: summaryData } = useGetMentorMonthlySummary(mentorshipId, {
    enabled: !videoId,
  });

  const summary = videoId
    ? mentorUpdate?.data?.update?.summary
    : summaryData?.data?.summary;

  const [deviceError, setDeviceError] = useState<string | null>(null);

  const createUpdateMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      return fetchApi({
        url: `/v1/mentorships/${mentorshipId}/create-update`,
        method: "POST",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
  });

  useEffect(() => {
    if (!canUpdateMentorships || videoId) return;
    if (devicesByType?.video?.length) {
      setVideoDeviceId(devicesByType.video[0].deviceId);
    }
    if (devicesByType?.audio?.length) {
      setAudioDeviceId(devicesByType.audio[0].deviceId);
    }
  }, [canUpdateMentorships, devicesByType, videoId]);

  useEffect(() => {
    if (!canUpdateMentorships || videoId) return;
    // Add timeout to check for devices
    const timeoutId = setTimeout(() => {
      if (!devicesByType?.video?.length || !devicesByType?.audio?.length) {
        setDeviceError(
          "No camera or microphone detected. Please ensure your devices are properly connected and permissions are granted.",
        );
      }
    }, 10000); // 10 seconds timeout

    return () => clearTimeout(timeoutId);
  }, [canUpdateMentorships, devicesByType, videoId]);

  const initializeCamera = async () => {
    try {
      if (activeRecordings.length) {
        await clearAllRecordings();
      }
      const recording = await createRecording();
      if (recording) {
        setCurrentRecordingId(recording.id);
        await openCamera(recording.id);
      }
    } catch (error) {
      console.error("Error initializing camera:", error);
    }
  };

  useEffect(() => {
    if (!canUpdateMentorships || videoId) return;
    // Only initialize if we have both device IDs
    if (videoDeviceId && audioDeviceId) {
      console.log("initializing camera with devices:", {
        videoDeviceId,
        audioDeviceId,
      });
      initializeCamera();
    }

    return () => {
      if (currentRecordingId) {
        closeCamera(currentRecordingId);
        clearAllRecordings();
      }
    };
  }, [canUpdateMentorships, videoId, videoDeviceId, audioDeviceId]); // Only depend on device IDs

  useEffect(() => {
    if (recording && !isPaused) {
      const id = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
      setIntervalId(id);
    } else if ((!recording || isPaused) && intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [recording, isPaused]);

  const handleStartRecording = async () => {
    if (currentRecordingId) {
      setShowDeviceModal(false);
      // Start countdown
      setCountdown(3);

      // Create countdown timer
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(countdownInterval);
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      // Wait for countdown to finish
      setTimeout(async () => {
        await startRecording(currentRecordingId);
        setRecording(true);
      }, 3000);
    }
  };

  const handleStopRecording = async () => {
    if (currentRecordingId) {
      await stopRecording(currentRecordingId);
      await closeCamera(currentRecordingId);
      setRecording(false);
      setIsReplayMode(true);
      setShowRightPanel(false);
    }
  };

  const handlePauseResumeRecording = async () => {
    if (currentRecordingId) {
      if (!isPaused) {
        await pauseRecording(currentRecordingId);
        setIsPaused(true);
        // Stop the duration timer while paused
        if (intervalId) {
          clearInterval(intervalId);
          setIntervalId(null);
        }
      } else {
        await resumeRecording(currentRecordingId);
        setIsPaused(false);
        // Resume the duration timer
        const id = setInterval(() => {
          setDuration((prev) => prev + 1);
        }, 1000);
        setIntervalId(id);
      }
    }
  };

  const handleRecordAgain = async () => {
    await clearAllRecordings();
    const recording = await createRecording();
    if (recording) {
      setCurrentRecordingId(recording.id);
      await openCamera(recording.id);
    }
    setDuration(0);
    setShowDeviceModal(true);
    setIsReplayMode(false);
    initializeCamera();
  };

  const handleSend = async () => {
    if (!canUpdateMentorships) return;
    setShowSendConfirmation(true);
  };

  const handleConfirmSend = async () => {
    if (!canUpdateMentorships) return;
    setIsUploading(true);
    try {
      const recording = activeRecordings.find(
        (r) => r.id === currentRecordingId,
      );

      if (!recording || !recording.blob) {
        return;
      }

      const blob = recording.blob;
      const file = new File([blob], "video.mp4", { type: "video/mp4" });

      const formData = new FormData();
      formData.append("data", file);
      formData.append("summary", summary);

      await createUpdateMutation.mutateAsync(formData);

      setShowSendConfirmation(false);
      navigate({ to: "/conversations" });
    } catch (error) {
      console.error("Upload error:", error);
      alert(
        "Failed to upload video. Please ensure you have permission to upload updates for this mentorship.",
      );
    } finally {
      setIsUploading(false);
      setShowSendConfirmation(false);
      setIsUploading(false);
    }
  };

  const currentRecording = activeRecordings.find(
    (r) => r.id === currentRecordingId,
  );

  if (isPending) {
    return (
      <div className="h-screen-nav flex h-screen w-full items-center justify-center bg-primary-100">
        <Loader2 className="h-12 w-12 animate-spin text-blue-brand" />
      </div>
    );
  }

  if (isError || !mentorship) {
    return (
      <div className="h-screen-nav flex h-screen w-full items-center justify-center bg-primary-100">
        <div className="text-blue-brand">Some errors occurred!</div>
      </div>
    );
  }

  if (!videoId && canUpdateMentorships && (!videoDeviceId || !audioDeviceId)) {
    return (
      <div className="h-screen-nav flex h-screen w-full flex-col items-center justify-center gap-4 bg-primary-100">
        {deviceError ? (
          <div className="text-center text-red-600">{deviceError}</div>
        ) : (
          <Loader2 className="h-12 w-12 animate-spin text-blue-brand" />
        )}
      </div>
    );
  }

  const menteeDisplayName = getMenteeDisplayName(mentorship.mentee);

  return (
    <div className="flex h-screen">
      <div className="relative flex-1 bg-gray-900">
        <RecordingPanel
          showDeviceModal={showDeviceModal}
          videoDeviceId={videoDeviceId}
          audioDeviceId={audioDeviceId}
          setVideoDeviceId={setVideoDeviceId}
          setAudioDeviceId={setAudioDeviceId}
          handleStartRecording={handleStartRecording}
          devicesByType={devicesByType}
          currentRecording={currentRecording}
          countdown={countdown}
          isReplayMode={isReplayMode}
          recording={recording}
          duration={duration}
          isPaused={isPaused}
          handleStopRecording={handleStopRecording}
          handlePauseResumeRecording={handlePauseResumeRecording}
          handleDownload={() => download(currentRecordingId!)}
          handleSend={handleSend}
          handleRecordAgain={handleRecordAgain}
          videoUrl={signedVideoUrl}
          canUpdateMentorships={canUpdateMentorships}
        />
      </div>
      <MentorUpdatesRightPanel
        menteeName={menteeDisplayName}
        summary={summary}
        isVisible={showRightPanel}
        toggleShowRightPanel={() => setShowRightPanel(!showRightPanel)}
        accessRecords={accessRecords?.data?.records || []}
        mentorshipId={mentorshipId}
      />
      {canUpdateMentorships && (
        <SendConfirmationModal
          showSendConfirmation={showSendConfirmation}
          setShowSendConfirmation={setShowSendConfirmation}
          onConfirmSend={handleConfirmSend}
          isUploading={isUploading}
          recipientName={menteeDisplayName}
        />
      )}
    </div>
  );
}
